import {
  Notification,
  NotificationContext,
  NotificationRowResponse,
  NotificationType,
  Shipment,
  Stop,
  QUERY_KEYS,
} from "@cnx/common";
import { Box, Button, Center, Flex, Loader } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateNotificationStatus } from "hooks/useNotifications";
import { useTripsLinks } from "hooks/useTripsNavigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NotificationService from "services/NotificationService";
import ShipmentService from "services/ShipmentService";
import StopService from "services/StopService";
import TripCardPreview from "../TripCardPreview";
import ChangeCard from "./ChangeCard";
import * as Sentry from "@sentry/react";

interface ChangeViewerProps {
  notification: Notification;
  goBack: () => void;
}

const ChangeViewer: React.FC<ChangeViewerProps> = ({
  notification,
  goBack,
}) => {
  const { t } = useTranslation("notifications");

  const {
    id,
    contextId: notificationContextId,
    context,
    confirmedDateTime,
  } = notification;

  const [changes, setChanges] = useState<NotificationRowResponse>([]);
  const [trip, setTrip] = useState<Shipment>();
  const [stop, setStop] = useState<Stop>();
  const [isError, setIsError] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!notification.id) return;

    const fetchNotificationRows = async () => {
      try {
        setIsPending(true);
        if (notification.context === NotificationContext.RESOURCE_COMBINATION) {
          const trip = await ShipmentService.getShipmentByResourceCombinationId(
            notificationContextId
          );
          setTrip(trip);
        }

        if (notification.context === NotificationContext.STOP) {
          const stop = await StopService.getStopByStopId(notificationContextId);
          setStop(stop);
        }
        const changes = await NotificationService.getChanges(notification.id);
        setChanges(changes);
      } catch (error) {
        setIsError(true);
        Sentry.captureException(error);
      } finally {
        setIsPending(false);
      }
    };

    fetchNotificationRows();
  }, [notification.id]);

  const updateNotificationStatus = useUpdateNotificationStatus();

  const confirmed = confirmedDateTime !== null;

  const viewed = notification.viewedDateTime !== null;
  const { getTripsLink, getStopShipmentsLink, getTripStopsLink } =
    useTripsLinks();

  const linkToStopShipments = getStopShipmentsLink({
    resourceCombinationId: stop?.resourceCombinationId,
    stopId: notificationContextId,
    completed: !stop?.hasUnfinishedActivities,
  });

  const linkToTripStops = getTripStopsLink({
    resourceCombinationId: notificationContextId,
    completed: !trip?.hasUnfinishedStops,
  });

  const linkToTrips = getTripsLink({ completed: !trip?.hasUnfinishedStops });

  const handleViewNotification = async () => {
    await updateNotificationStatus(id, { viewed: true, confirmed: !confirmed });
    switch (context) {
      case NotificationContext.STOP:
        window.location.replace(linkToStopShipments);
        break;
      case NotificationContext.RESOURCE_COMBINATION:
        if (notification.notificationTypeKey === NotificationType.NEW_TRIP) {
          window.location.replace(linkToTrips);
        } else {
          window.location.replace(linkToTripStops);
        }
        break;
      default:
        break;
    }
  };

  const handleConfirmNotification = async () => {
    await updateNotificationStatus(id, { viewed: !viewed, confirmed: true });
    goBack();
  };

  const handleErrorNotification = async () => {
    await NotificationService.archiveNotification(notification.id);
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.NOTIFICATIONS],
    });
    goBack();
  };

  useEffect(() => {
    isError && handleErrorNotification();
  }, [isError]);

  if (isPending) {
    return (
      <Center w={"100%"} h={"20vh"}>
        <Loader />
      </Center>
    );
  }

  const isShowTripCard =
    trip && notification.notificationTypeKey === NotificationType.NEW_TRIP;

  const openButtonLabel = () => {
    switch (context) {
      case NotificationContext.STOP:
        return t("openShipments");
      case NotificationContext.RESOURCE_COMBINATION:
        if (notification.notificationTypeKey === NotificationType.NEW_TRIP) {
          return t("openTrips");
        } else {
          return t("openStops");
        }
      default:
        return t("open");
    }
  };

  return (
    <Box>
      <Flex direction={"column"} justify={"space-between"}>
        <div>
          {isShowTripCard && <TripCardPreview trip={trip} />}
          <ul className="border-b border-gray">
            {changes.map(changes => {
              return (
                <li key={changes.rowTypeKey} className={"border-t border-gray"}>
                  <ChangeCard notificationRows={changes.notificationRows} />
                </li>
              );
            })}
          </ul>
        </div>
        <Flex p={20} justify={"space-between"}>
          <Button
            onClick={handleViewNotification}
            tt="uppercase"
            color={"blue"}
          >
            {openButtonLabel()}
          </Button>
          {notification.confirmation ? (
            <Button
              onClick={handleConfirmNotification}
              tt="uppercase"
              color={confirmed ? "gray" : "green"}
              disabled={confirmed}
              variant={confirmed ? "outline" : "filled"}
            >
              {confirmed ? t("confirmed") : t("confirm")}
            </Button>
          ) : (
            <></>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default ChangeViewer;
